import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import LoadingScreen from './LoadingScreen';
import { useSettings } from '../contexts/SettingsContext';

interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'fade' | 'slide';
  duration?: number;
  delay?: number;
}

// Simplified animation variants
const variants = {
  fade: {
    initial: { opacity: 0 },
    in: { opacity: 1 },
    out: { opacity: 0 },
  },
  slide: {
    initial: { opacity: 0, x: -10 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: 10 },
  }
};

// Simplified transition type based on the route change
const getRouteTransition = (pathname: string) => {
  if (pathname.includes('admin')) {
    return 'fade';
  }
  return 'slide';
};

const PageTransition: React.FC<PageTransitionProps> = ({ 
  children, 
  className = '', 
  variant, 
  duration = 0.2,
  delay = 0
}) => {
  const location = useLocation();
  const [routeTransition, setRouteTransition] = useState(variant || getRouteTransition(location.pathname));
  const { settings } = useSettings();

  // Safely access loading screen settings with defaults
  const loadingScreenEnabled = settings?.general?.loadingScreen?.enabled ?? true;
  const transitionDuration = settings?.general?.loadingScreen?.transitionDuration ?? 500;

  // Update transition type when route changes if variant is not explicitly set
  useEffect(() => {
    if (!variant) {
      setRouteTransition(getRouteTransition(location.pathname));
    }
  }, [location.pathname, variant]);
  
  const selectedVariant = variants[routeTransition];
  
  const transition = {
    type: 'tween',
    ease: 'easeOut',
    duration,
    delay
  };

  if (!loadingScreenEnabled) {
    return <>{children}</>;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: transitionDuration / 1000 }}
        className={`page-transition ${className}`}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default PageTransition; 